import { datadogRum } from '@datadog/browser-rum';
import { DependencyList, EffectCallback, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { messageLog } from 'utilities/helpers/logger/message-logger';
import { PageChangeContext } from 'utilities/hooks/page-change-context.js';
import { predicateBoolean } from 'utilities/isType';

/** Only renders after second render, e.g. not when being initialised */
function UseEffectNotInitialRender(effect: EffectCallback, deps: DependencyList) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      return effect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const AdDomReadyHook = ({ hasAd, adReadyCB }: { hasAd?: boolean; adReadyCB: () => void }) => {
  const { pageUrlChanged } = useContext(PageChangeContext);
  const [adDomReady, setAdDomReady] = useState(false);

  useEffect(() => {
    if (pageUrlChanged) {
      /** Page navigated; reset the state of the ad as the ad has reset */
      setAdDomReady(false);
    }
  }, [pageUrlChanged]);

  UseEffectNotInitialRender(() => {
    if (predicateBoolean(hasAd)) {
      /** We don't want to run until hasAd is defined e.g. a boolean */
      if (hasAd && adDomReady) {
        /** If ad is ready, we call the callback */
        adReadyCB();
      }
    }
  }, [hasAd, adDomReady, adReadyCB]);

  return { setAdDomReady };
};

export const UseAdCallbacks = ({ rendersAd, name }: { rendersAd?: boolean; name: string }) => {
  const fn = useCallback(
    (suffix: string) => {
      messageLog.trace(`Log Fuse ${name}: ${suffix}`);
      datadogRum.addTiming(`Log_Fuse_${name}_${suffix}`);
    },
    [name],
  );

  /** @view.custom_timings.Log_Fuse_Banner_Dom_Ready:* */
  const adDomReadyCB = useCallback(() => fn('Dom_Ready'), [fn]);
  const { setAdDomReady: setAdDomReady } = AdDomReadyHook({
    hasAd: rendersAd,
    adReadyCB: adDomReadyCB,
  });

  const adRequestCB = useCallback(() => fn('Register_TagId'), [fn]);
  const { setAdDomReady: setAdRequest } = AdDomReadyHook({
    hasAd: rendersAd,
    adReadyCB: adRequestCB,
  });

  const adResponseCB = useCallback(() => fn('Ad_Response'), [fn]);
  const { setAdDomReady: setAdResponse } = AdDomReadyHook({
    hasAd: rendersAd,
    adReadyCB: adResponseCB,
  });

  const adCBs = useMemo(() => {
    return {
      adDomReady: setAdDomReady,
      adRequest: setAdRequest,
      adResponse: setAdResponse,
    };
  }, [setAdDomReady, setAdRequest, setAdResponse]);

  return { adCBs };
};
